import { useState } from 'react';
import { prepareFileListForMedia } from '../../../../utils/prepareImageFileForUpload';

const MAX_FILES = 10;

const MAX_FILE_SIZES = {
    image: 10 * 1024 * 1024,
    video: 50 * 1024 * 1024,
    audio: 20 * 1024 * 1024,
};

const getMediaTypeFromFile = (file) => {
    const mimeType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();

    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';

    if (fileName.match(/\.(jpg|jpeg|png|gif|heic|heif|tiff)$/)) return 'image';
    if (fileName.match(/\.(mp4|mov|m4v|hevc)$/)) return 'video';
    if (fileName.match(/\.(m4a|caf|mp3|wav|aac)$/)) return 'audio';

    return 'image';
};

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

export const useTmpMediaList = () => {
    const [tmpMediaList, setTmpMediaList] = useState([]);

    const tmpAddFiles = async (fileList) => {
        const newFiles = await prepareFileListForMedia(fileList);

        if (tmpMediaList.length + newFiles.length > MAX_FILES) {
            throw new Error(`ファイル数は最大${MAX_FILES}個までです。現在${tmpMediaList.length}個のファイルがあります。`);
        }

        for (const file of newFiles) {
            const mediaType = getMediaTypeFromFile(file);
            const maxSize = MAX_FILE_SIZES[mediaType] || MAX_FILE_SIZES.image;
            if (file.size > maxSize) {
                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
                const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
                const mediaTypeName = mediaType === 'image' ? '画像' : mediaType === 'video' ? '動画' : '音声';
                throw new Error(`${mediaTypeName}ファイルは${maxSizeMB}MB以下にしてください。選択されたファイル: ${fileSizeMB}MB`);
            }
        }

        setTmpMediaList((prev) => {
            const filtered = newFiles.filter(
                (file) =>
                    !prev.some(
                        (p) =>
                            p?.file &&
                            p.file.name === file.name &&
                            p.file.size === file.size &&
                            p.file.type === file.type
                    )
            );
            const wrapped = filtered.map((file) => ({
                id: generateId(),
                file,
                name: '',
                description: '',
                media_type: file.type,
                mediaType: getMediaTypeFromFile(file),
                url: null,
            }));
            return [...prev, ...wrapped];
        });
    };

    const tmpRemoveFileAt = (index) => {
        setTmpMediaList((prev) => {
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    };

    const tmpUpdateFileMeta = (index, key, value) => {
        setTmpMediaList((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [key]: value };
            return next;
        });
    };

    const clearTmpMediaList = () => setTmpMediaList([]);

    return {
        tmpMediaList,
        tmpAddFiles,
        tmpRemoveFileAt,
        tmpUpdateFileMeta,
        clearTmpMediaList,
    };
};
